import type { ReactNode } from 'react';
import { Button, Tag, Tooltip } from 'antd';
import {
  UserOutlined,
  BankOutlined,
  EnvironmentOutlined,
  GiftOutlined,
  EditOutlined,
  CloseOutlined,
  TeamOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import type { UserType } from '../../types';

interface Props {
  name: string;
  phone: string;
  address: string;
  userType?: UserType;
  onEdit: () => void;
  onClear: () => void;
  /**
   * Mở modal khuyến mãi. Không truyền ⇒ KHÔNG render icon quà — thẻ không nên
   * phụ thuộc vào một tính năng mà nơi gọi sau này có thể không có.
   */
  onVoucher?: () => void;
  /**
   * TCR-1281: tên nhóm khách hàng (vd "Đại lý cấp 2") — đọc từ
   * `item.user.group.name` lúc chọn khách (`/pos/customers` search). Cùng quy
   * ước với `onVoucher`/`loyalty`: không truyền hoặc rỗng ⇒ KHÔNG render badge
   * — khách chưa thuộc nhóm nào là trạng thái hợp lệ, không phải lỗi thiếu dữ
   * liệu. Dùng `Tag color="purple"` khớp đúng màu badge nhóm đã có sẵn trong
   * dropdown gợi ý (`CustomerSearchField.tsx`) để thẻ khách đã chọn và ô tìm
   * kiếm nói cùng một ngôn ngữ màu.
   */
  groupName?: string;
  /**
   * TCR-1953 (QA reopen 23/9) — mã KH AMIS (`amis_account_object_code`). `undefined` ⇒ không
   * render dòng này (màn sửa đơn không có dữ liệu); chuỗi rỗng ⇒ hiện "—" (khách chưa có trên AMIS).
   */
  amisCode?: string;
  /** Thẻ điểm tích lũy (LoyaltyBadge). */
  loyalty?: ReactNode;
  /** Nút phụ dưới cùng — vd nút hiện CCCD gốc ở trang sửa đơn. */
  extraAction?: ReactNode;
  /**
   * TCR-1818 (fix vòng 3, PO đánh FAILED bản cảnh báo dạng banner/text "lẫn vào các dòng,
   * khó để ý") — khách B2B đang thực sự vượt hạn mức công nợ. Card chỉ còn hiện MỘT Tag đỏ
   * gọn (đúng pattern "Quá công nợ" đã dùng ở danh sách đơn hàng, `useOrderColumns.tsx`),
   * bấm vào mở lại đúng pop-up cảnh báo độc lập — nguồn cảnh báo chính giờ là pop-up hiện
   * ngay lúc chọn khách (`CustomerSidebar/index.tsx`), Tag này chỉ là lối vào lại.
   */
  isOverLimit?: boolean;
  /** Bấm vào Tag "Vượt hạn mức công nợ" — mở lại pop-up cảnh báo. */
  onOverLimitClick?: () => void;
}

const ICON_BTN = { width: 28, height: 28 } as const;

/**
 * Thẻ khách đã chọn: hai dòng + một hàng icon.
 *
 * Hàng "Tìm khách hàng" bị ẩn khi thẻ này hiện (xem `CustomerSidebar`), nên icon
 * xoá ở đây là đường DUY NHẤT để đổi sang khách khác. Cố ý: chọn đè khách trong
 * khi đơn đang mang voucher/điểm tích luỹ của khách cũ là lỗi im lặng.
 *
 * Dùng chung với trang `Orders/edit` — mọi thay đổi ở đây đổi cả hai màn.
 */
export function SelectedCustomerCard({
  name,
  phone,
  address,
  userType,
  onEdit,
  onClear,
  onVoucher,
  groupName,
  amisCode,
  loyalty,
  extraAction,
  isOverLimit,
  onOverLimitClick,
}: Props) {
  const CustomerTypeIcon = userType === 'organization' ? BankOutlined : UserOutlined;
  const customerTypeLabel = userType === 'organization' ? 'Khách hàng tổ chức' : 'Khách hàng cá nhân';

  return (
    <div
      style={{
        border: '1px solid #e0e0e0',
        borderRadius: 6,
        padding: '6px 8px',
        background: '#fff',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <CustomerTypeIcon
          aria-label={customerTypeLabel}
          style={{ color: '#8c8c8c', flexShrink: 0 }}
        />
        {/* Tên và SĐT là MỘT nhóm: SĐT phải nằm sát ngay sau tên chứ không bị
            đẩy sang mép phải. Nhóm `flex-wrap` nên khi chật thì SĐT xuống dòng,
            còn tên hiện đủ — đánh đổi người dùng đã chọn: đọc đủ tên quan trọng
            hơn giữ thẻ đúng một dòng. */}
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            columnGap: 4,
          }}
        >
          <span data-testid="customer-name" style={{ fontWeight: 700, fontSize: 13 }}>
            {name || 'Khách hàng'}
          </span>
          {phone && (
            <span data-testid="customer-phone" style={{ fontSize: 13, color: '#1677ff' }}>
              - {phone}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', flexShrink: 0 }}>
          {onVoucher && (
            <Tooltip title="Chọn khuyến mãi / Voucher">
              <Button
                type="text"
                size="small"
                aria-label="Chọn khuyến mãi / Voucher"
                icon={<GiftOutlined />}
                onClick={onVoucher}
                style={ICON_BTN}
              />
            </Tooltip>
          )}
          <Tooltip title="Sửa thông tin khách hàng">
            <Button
              type="text"
              size="small"
              aria-label="Sửa thông tin khách hàng"
              icon={<EditOutlined />}
              onClick={onEdit}
              style={ICON_BTN}
            />
          </Tooltip>
          {/* CloseOutlined chứ không phải DeleteOutlined: đây là BỎ CHỌN khách
              khỏi đơn, không phải xoá khách khỏi hệ thống. `aria-label` giữ
              nguyên chuỗi cũ để test và screen reader không phải đổi theo. */}
          <Tooltip title="Xoá khách hàng">
            <Button
              type="text"
              size="small"
              aria-label="Xoá khách hàng"
              icon={<CloseOutlined />}
              onClick={onClear}
              style={ICON_BTN}
            />
          </Tooltip>
        </div>
      </div>

      {/* TCR-1281: badge nhóm khách hàng — độc lập với `loyalty` (điểm/hạng có
          thể null khi khách chưa phát sinh giao dịch, nhưng khách vẫn có thể
          thuộc một nhóm), nên đặt riêng ngay dưới tên/SĐT, KHÔNG lồng vào
          `{loyalty}` (có thể là `null`). */}
      {groupName && (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Tag
            data-testid="customer-group-badge"
            icon={<TeamOutlined />}
            color="purple"
            style={{ margin: 0 }}
          >
            {groupName}
          </Tag>
        </div>
      )}

      {amisCode !== undefined && (
        <div data-testid="customer-amis-code" style={{ fontSize: 12, color: '#666' }}>
          Mã KH AMIS: <strong style={{ color: '#262626' }}>{amisCode || '—'}</strong>
        </div>
      )}

      {address && (
        <div
          data-testid="customer-address-row"
          style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 12, color: '#666' }}
        >
          <EnvironmentOutlined style={{ flexShrink: 0, marginTop: 3 }} />
          <span style={{ minWidth: 0 }}>{address}</span>
        </div>
      )}

      {/* TCR-1818 — Tag đỏ gọn thay cho banner/text inline cũ (PO đánh FAILED vì "lẫn vào các
          dòng, khó để ý"). Nguồn cảnh báo chính là pop-up độc lập hiện lúc chọn khách; Tag này
          chỉ là dấu vết còn lại + lối bấm vào xem lại pop-up đó. Không chặn lên đơn — khác nút
          "Duyệt đơn" ở TCR-1823 (có chặn thật theo quyền). */}
      {isOverLimit && (
        <div>
          <Tag
            data-testid="customer-over-limit-tag"
            icon={<ExclamationCircleOutlined />}
            color="red"
            style={{ margin: 0, cursor: onOverLimitClick ? 'pointer' : undefined }}
            onClick={onOverLimitClick}
          >
            Vượt hạn mức công nợ
          </Tag>
        </div>
      )}

      {loyalty}
      {extraAction}
    </div>
  );
}
